import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import {
  TopBarSection,
  TopBarColumn,
} from "@src/components/PageTopSection/PageTopSection";
import PageTitle from "@src/components/PageTitle";
import { device, theme } from "@src/theme";
import styled from "styled-components";
import GoBackButton from "@src/components/GoBackButton";
import { UIButton } from "@src/components/UIButton";
import { useNavigate, useParams } from "react-router-dom";
import UploadIcon from "@src/assets/img/picture-icon.svg";
import CheckOffSVG from "@src/assets/img/check-rounded-off.svg";
import CheckSVG from "@src/assets/img/check-rounded.svg";
import SaveIcon from "@src/assets/img/save-icon.svg";
import EyeIcon from "@src/assets/img/eye-icon-yellow.svg";
import GoBackWebsiteIcon from "@src/assets/img/go-back-website.svg";
import RichTextEditor from "@src/components/RichTextEditor/RichTextEditor";
import {
  createArticleApi,
  updateArticleApi,
  uploadArticleImage,
  CreateArticleBody,
  ArticleStatus,
  IArticle,
} from "@src/apis/articleApis";
import { useNotification } from "@src/context/NotificationContext";
import Spinner from "@src/components/Spinner";
import ImageUploadModal from "@src/components/ImageUploadModal/ImageUploadModal";
import useModal from "@src/hooks/useModal";
import { useBeforeUnload } from "@src/hooks/useBeforeUnload";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { useArticle } from "@src/queries/articleQueries";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { buildMediaUrl } from "@src/utils/mediaUrl";
import { normalizeArticleHtml } from "@src/utils/articleRichText";
import dayjs from "dayjs";
import { Signal, Wifi, Battery } from "lucide-react";
import { RovrDuotoneImage } from "@src/components/RovrDuotoneImage";

// Helper function to extract plain text from HTML and trim to specified length
const extractPlainText = (html: string, maxLength: number = 150): string => {
  if (!html) return "";
  const decoder = document.createElement("textarea");
  decoder.innerHTML = html;
  const decodedHtml = decoder.value;
  const div = document.createElement("div");
  div.innerHTML = decodedHtml;
  const plainText = (div.textContent || div.innerText || "")
    .replace(/\s+/g, " ")
    .trim();
  return plainText.length > maxLength
    ? plainText.substring(0, maxLength).trim()
    : plainText;
};
import UIModal from "@src/components/UIModal/UIModal";
import { SearchInput } from "@src/components/SearchInput";
import { useDebounce } from "@src/hooks/useDebounce";
import { useInfinitePlaylists } from "@src/queries/playlistQueries";
import { IPlaylistRecord } from "@src/types/playlist";
import { useMyCurators } from "@src/hooks/useMyCurators";
import ArtWorkImage from "@src/components/ArtWorkImage";
import RovrLogo from "@assets/img/login-logo.svg";
import ArticleSVG from "@src/assets/ArticleSVG";

const DescriptionText = styled.p`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 14px;
  margin: 0;
  margin-left: ${({ theme }): string => theme.margins.medium};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  max-width: 390px;
  gap: ${({ theme }): string => theme.margins.medium};
`;

const MainContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  margin: 0;
  min-height: fit-content;
  box-sizing: border-box;
`;

const EditorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.large};
  width: 100%;
  max-width: 100%;
  min-height: fit-content;
  align-items: flex-start;
  box-sizing: border-box;
`;

const ArticleLayout = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr 360px;
  gap: ${({ theme }): string => theme.margins.large};
  width: 100%;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const RelatedPlaylistsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.small};
  width: 100%;
`;

const RelatedPlaylistsLabel = styled.span`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const RelatedPlaylistCard = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.xsmall};
  width: 100%;
  min-height: 120px;
  border-radius: 10px;
  border: 1px dashed ${({ theme }): string => theme.colors.gray};
  background: transparent;
  color: ${({ theme }): string => theme.colors.gray};
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }): string => theme.colors.yellow};
  }
`;

const RelatedPlaylistImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  object-fit: cover;
`;

const RelatedPlaylistTitle = styled.span`
  color: ${({ theme }): string => theme.colors.white};
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const RelatedPlaylistSubtitle = styled.span`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 12px;
  text-align: center;
`;

const PlaylistListContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.small};
  padding-right: ${({ theme }): string => theme.margins.small};
`;

const PlaylistListItem = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: ${({ theme }): string => theme.margins.small};
  border-radius: 8px;
  border: 1px solid
    ${({ $active, theme }): string =>
      $active ? theme.colors.yellow : theme.colors.gray};
  background-color: ${({ $active, theme }): string =>
    $active ? theme.colors.opacity_yellow : theme.colors.darkBg};
  color: ${({ theme }): string => theme.colors.white};
  cursor: pointer;
  text-align: left;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
  //blend color with opacity_yellow

  &:hover {
    background-color: ${({ theme }): string => theme.colors.opacity_yellow};
    border-color: ${({ theme }): string => theme.colors.yellow};
  }
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.medium};
  width: 360px;
  min-width: 360px;

  @media ${device.tablet} {
    display: none;
  }
  @media ${device.mobile} {
    display: none;
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.small};
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const PreviewDevice = styled.div`
  background-color: ${({ theme }): string => theme.colors.black};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  height: 680px;
  display: flex;
  flex-direction: column;
  min-width: 320px;
`;

const PreviewPhoneStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }): string => theme.colors.white};
  background-color: ${({ theme }): string => theme.colors.black};
`;

const PreviewPhoneAppBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }): string => theme.margins.medium}
    ${({ theme }): string => theme.margins.medium};
  background-color: ${({ theme }): string => theme.colors.black};
`;

const PreviewPhoneBack = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PreviewPhoneContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 ${({ theme }): string => theme.margins.medium}
    ${({ theme }): string => theme.margins.medium};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }): string => theme.margins.small};
`;

const PreviewPhoneBottomTabs = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  padding: 12px 0 16px;
  background-color: ${({ theme }): string => theme.colors.black};
  border-top: 1px solid ${({ theme }): string => theme.colors.yellow};
  font-size: 10px;
  font-weight: 500;
  color: ${({ theme }): string => theme.colors.yellow};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 0 0 16px 16px;
`;

const PreviewTab = styled.span<{ $active?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  color: ${({ theme }): string => theme.colors.yellow};
  background-color: transparent;
  border-right: 1px solid ${({ theme }): string => theme.colors.yellow};
  font-weight: ${({ $active }): number => ($active ? 700 : 500)};
  opacity: ${({ $active }): number => ($active ? 1 : 0.85)};
  &:last-of-type {
    border-right: none;
  }
`;

const PreviewCoverWrapper = styled.div`
  width: 100%;
  min-width: 280px;
  height: 200px;
  min-height: 200px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
`;

const PreviewTitle = styled.div`
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const PreviewMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.small};
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.4;
`;

const PreviewByline = styled.div`
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  & .by-prefix {
    font-size: 11px;
    font-weight: 400;
  }
  & .by-name {
    font-size: 13px;
    font-style: italic;
  }
`;

const PreviewCaption = styled.div`
  color: ${({ theme }): string => theme.colors.gray};
  font-size: 12px;
  line-height: 1.4;
  margin-top: ${({ theme }): string => theme.margins.small};
`;

const PreviewBody = styled.div`
  color: ${({ theme }): string => theme.colors.yellow};
  font-size: 18px;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;

  * {
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  p {
    margin: 0 0 16px;
    color: ${({ theme }): string => theme.colors.yellow};
    font-size: 18px;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  span.text-small {
    font-size: 10px;
  }
  span.text-medium {
    font-size: 30px;
  }
  span.text-large {
    font-size: 40px;
  }
  span.text-huge {
    font-size: 48px;
  }
  span.heading {
    display: block;
  }
  b {
    font-weight: 700;
  }
  i {
    font-style: italic;
  }
  a {
    color: ${({ theme }): string => theme.colors.yellow};
    text-decoration: underline;
  }
  img {
    width: 100%;
    border-radius: 12px;
    display: block;
    margin: 16px 0;
  }
  hr {
    border: 0;
    border-top: 1px solid ${({ theme }): string => theme.colors.gray};
    margin: 24px 0;
  }
`;

const TitleInput = styled.input`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }): string => theme.colors.gray};
  color: ${({ theme }): string => theme.colors.white};
  font-size: 32px;
  font-weight: bold;
  padding: ${({ theme }): string => theme.margins.medium} 0;
  width: 100%;
  max-width: 100%;
  outline: none;
  margin-bottom: ${({ theme }): string => theme.margins.medium};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }): string => theme.colors.gray};
  }

  &:focus {
    border-bottom-color: ${({ theme }): string => theme.colors.gray};
  }

  &[readonly],
  &:disabled {
    opacity: 0.75;
    cursor: default;
    border-bottom: none;
  }
`;

const CoverPhotoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.medium};
  padding: ${({ theme }): string => theme.margins.medium} 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const CoverPhotoButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.small};
  background: transparent;
  border: 1px solid ${({ theme }): string => theme.colors.gray};
  border-radius: 24px;
  padding: ${({ theme }): string => theme.margins.medium};
  color: ${({ theme }): string => theme.colors.gray};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  max-width: 465px;
  height: 25px;
  &:hover {
    background-color: ${({ theme }): string => theme.colors.darkGray};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ImageInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }): string => theme.margins.small};
  justify-content: space-between;
  width: 100%;
`;

const CoverPhotoPreview = styled.div<{ $imageUrl?: string }>`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 8px;
  background-color: ${({ theme }): string => theme.colors.darkBg};
  background-image: ${({ $imageUrl }): string =>
    $imageUrl ? `url(${$imageUrl})` : "none"};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const schema = yup.object({
  title: yup.string().required("Article title is required"),
  body: yup.string().required("Article body is required"),
});

type ArticleFormValues = {
  title: string;
  body: string;
};

const WriteArticlePage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const curator = useAuthCurator();
  const role = useRole();
  const { articleId: articleIdParam } = useParams<{ articleId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false); // cover
  const [uploadingEditorImage, setUploadingEditorImage] = useState(false); // inline
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadedImageId, setUploadedImageId] = useState<number | null>(null);
  const [localImageFile, setLocalImageFile] = useState<File | null>(null);
  const [imageCaption, setImageCaption] = useState<string>("");
  const [articleId, setArticleId] = useState<string | null>(null);
  const [coverDirty, setCoverDirty] = useState(false);
  const [playlistDirty, setPlaylistDirty] = useState(false);
  const { isOpen: isModalOpen, openModal, closeModal } = useModal();
  const {
    isOpen: isPlaylistModalOpen,
    openModal: openPlaylistModal,
    closeModal: closePlaylistModal,
  } = useModal();
  const [playlistSearchText, setPlaylistSearchText] = useState("");
  const [selectedPlaylist, setSelectedPlaylist] = useState<{
    documentId: string;
    title?: string;
    showTitle?: string;
    imageUrl?: string;
  } | null>(null);
  const debouncedPlaylistSearch = useDebounce<string>(playlistSearchText, 400);
  const { myCurators } = useMyCurators(curator?.documentId || "", "", role);
  const subcuratorsIds = useMemo(
    () => myCurators.map((item) => item.documentId).filter(Boolean),
    [myCurators]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<ArticleFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { title: "", body: "" },
  });

  const watchedValues = watch();
  const canApprove = role === "admin" || role === "manager";
  const previewTitle = watchedValues.title?.trim() || "Title";
  const previewHtml = useMemo(() => {
    if (!watchedValues.body) return "";
    return normalizeArticleHtml(watchedValues.body);
  }, [watchedValues.body]);

  const previewBodySegments = useMemo(() => {
    const htmlWithWrappers = previewHtml
      ? previewHtml.replace(
          /<img([^>]*)\/?>/gi,
          '<div class="article-image-wrap"><img$1></div>'
        )
      : "";
    if (!htmlWithWrappers) return [];
    const segments: Array<
      | { type: "html"; content: string }
      | { type: "img"; src: string; alt: string }
    > = [];
    const imgBlockRegex =
      /<div\s+class="article-image-wrap"><img\s+([^>]+)\/?>\s*<\/div>/gi;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = imgBlockRegex.exec(htmlWithWrappers)) !== null) {
      const before = htmlWithWrappers.slice(lastIndex, match.index);
      if (before) segments.push({ type: "html", content: before });
      const attrs = match[1];
      const srcMatch = attrs.match(/src=["']([^"']+)["']/i);
      const altMatch = attrs.match(/alt=["']([^"']*)["']/i);
      segments.push({
        type: "img",
        src: buildMediaUrl(srcMatch?.[1] ?? ""),
        alt: altMatch?.[1] ?? "",
      });
      lastIndex = match.index + match[0].length;
    }
    const remaining = htmlWithWrappers.slice(lastIndex);
    if (remaining) segments.push({ type: "html", content: remaining });
    if (segments.length === 0 && htmlWithWrappers) {
      segments.push({ type: "html", content: htmlWithWrappers });
    }
    return segments;
  }, [previewHtml]);

  const previewBodyText = useMemo(() => {
    if (!previewHtml) return "";
    const div = document.createElement("div");
    div.innerHTML = previewHtml;
    return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
  }, [previewHtml]);
  const previewReadMinutes = useMemo(() => {
    const words = previewBodyText ? previewBodyText.split(/\s+/).length : 0;
    return Math.max(1, Math.ceil(words / 200));
  }, [previewBodyText]);
  const hasAnyContent = useMemo(() => {
    const hasTitle = Boolean(watchedValues.title?.trim());
    const hasBody =
      Boolean(watchedValues.body?.trim()) &&
      watchedValues.body?.trim() !== "<p><br></p>";
    const hasImage = Boolean(localImageFile || imagePreview);
    const hasPlaylist = Boolean(selectedPlaylist?.documentId);
    return hasTitle || hasBody || hasImage || hasPlaylist;
  }, [
    watchedValues.title,
    watchedValues.body,
    localImageFile,
    imagePreview,
    selectedPlaylist,
  ]);
  const isSaveRequired = !articleId || isDirty || coverDirty || playlistDirty;

  const {
    data: articleResponse,
    isLoading: isLoadingArticle,
    isError: isErrorArticle,
  } = useArticle(articleIdParam || "", { enabled: !!articleIdParam });

  const loadedArticle = (articleResponse as { data?: { data?: IArticle } })
    ?.data?.data;
  const loadedArticleStatus: ArticleStatus | undefined =
    loadedArticle?.articleStatus;
  const curatorArticleLocked =
    Boolean(articleIdParam) &&
    (loadedArticleStatus === "submitted" ||
      loadedArticleStatus === "published");

  useBeforeUnload({
    enabled:
      !curatorArticleLocked &&
      ((hasAnyContent && (isDirty || playlistDirty)) || coverDirty) &&
      !isSubmitting,
    message:
      "You have unsaved changes. Are you sure you want to leave? Your changes will be lost.",
  });

  const featuredImageFromArticle = useMemo(() => {
    const article = (articleResponse as any)?.data?.data;
    const featuredUrl =
      article?.featuredImage?.formats?.large?.url ||
      article?.featuredImage?.formats?.medium?.url ||
      article?.featuredImage?.formats?.small?.url ||
      article?.featuredImage?.formats?.thumbnail?.url ||
      article?.featuredImage?.url ||
      "";
    return featuredUrl ? buildMediaUrl(featuredUrl) : "";
  }, [articleResponse]);

  const previewCoverUrl = useMemo(() => {
    if (imagePreview) return imagePreview;
    if (featuredImageFromArticle) return featuredImageFromArticle;
    if (!previewHtml) return "";
    const div = document.createElement("div");
    div.innerHTML = previewHtml;
    const firstImg = div.querySelector("img");
    const src = firstImg?.getAttribute("src") || "";
    if (!src) return "";
    if (src.startsWith("data:")) return src;
    return buildMediaUrl(src);
  }, [imagePreview, featuredImageFromArticle, previewHtml]);

  const previewDate = useMemo(() => {
    const article = (articleResponse as any)?.data?.data;
    const dateStr =
      article?.publishedDate || article?.createdDate || article?.createdAt;
    if (dateStr) return dayjs(dateStr).format("MMM DD, YYYY");
    return dayjs().format("MMM DD, YYYY");
  }, [articleResponse]);

  useEffect(() => {
    if (!articleIdParam) return;
    const article = (articleResponse as any)?.data?.data;
    if (!article) return;

    setArticleId(article.documentId);
    reset({
      title: article.header || "",
      body: article.body || "",
    });
    if (article.playlist?.documentId) {
      const playlistImageUrl =
        article.playlist?.overwriteShowRadioImage?.url ||
        article.playlist?.show?.radioImage?.url ||
        "";
      setSelectedPlaylist({
        documentId: article.playlist.documentId,
        title: article.playlist.title,
        showTitle: article.playlist.show?.title,
        imageUrl: playlistImageUrl
          ? buildMediaUrl(playlistImageUrl)
          : undefined,
      });
    } else {
      setSelectedPlaylist(null);
    }
    setPlaylistDirty(false);

    const featuredUrl =
      article.featuredImage?.formats?.large?.url ||
      article.featuredImage?.formats?.medium?.url ||
      article.featuredImage?.formats?.thumbnail?.url ||
      article.featuredImage?.url;
    if (featuredUrl) {
      setImagePreview(buildMediaUrl(featuredUrl));
      setUploadedImageId(article.featuredImage?.id ?? null);
    } else {
      setImagePreview(null);
      setUploadedImageId(null);
    }
    setImageCaption(article.featuredImageCaption || "");
    setLocalImageFile(null);
    setCoverDirty(false);
  }, [articleIdParam, articleResponse, reset]);

  const {
    data: playlistsData,
    isLoading: isLoadingPlaylists,
    isFetchingNextPage: isFetchingNextPlaylists,
    fetchNextPage: fetchNextPlaylists,
    hasNextPage: hasNextPlaylists,
  } = useInfinitePlaylists(
    {
      searchText: debouncedPlaylistSearch,
      curatorId: canApprove ? "" : curator?.documentId || "",
      role,
      isSubcurator: curator?.isSubcurator,
      subcuratorsIds,
      sorting: { field: "updatedAt", direction: "desc" },
    },
    {
      enabled: isPlaylistModalOpen && (canApprove || !!curator?.documentId),
    }
  );

  const playlists = useMemo(() => {
    if (!playlistsData?.pages) return [];
    const allPlaylists: IPlaylistRecord[] = [];
    playlistsData.pages.forEach((page) => {
      const response = page as any;
      if (response?.data?.data) {
        allPlaylists.push(...response.data.data);
      }
    });
    return allPlaylists;
  }, [playlistsData]);

  const playlistsEndRef = React.useRef<HTMLDivElement | null>(null);

  const getPlaylistImageUrl = (playlist: Partial<IPlaylistRecord>): string => {
    const rawUrl =
      playlist.overwriteShowRadioImage?.url || playlist.show?.radioImage?.url;
    return rawUrl ? buildMediaUrl(rawUrl) : "";
  };
  useEffect(() => {
    if (!isPlaylistModalOpen || !playlistsEndRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          hasNextPlaylists &&
          !isFetchingNextPlaylists
        ) {
          observer.unobserve(target.target);
          fetchNextPlaylists().finally(() => {
            if (hasNextPlaylists) observer.observe(target.target);
          });
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );
    const currentRef = playlistsEndRef.current;
    observer.observe(currentRef);
    return () => {
      observer.unobserve(currentRef);
    };
  }, [
    fetchNextPlaylists,
    hasNextPlaylists,
    isFetchingNextPlaylists,
    isPlaylistModalOpen,
  ]);

  const handleImageSave = (file: File | null, caption?: string): void => {
    // Caption change should be treated as a dirty cover update even if no new file
    if ((caption ?? "") !== imageCaption) {
      setCoverDirty(true);
      setImageCaption(caption ?? "");
    }

    if (!file) {
      // No new file, just caption update
      return;
    }

    setLocalImageFile(file);
    setCoverDirty(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadInlineImage = useCallback(
    async (file: File) => {
      try {
        const response = await uploadArticleImage(file);
        const uploaded = response.data?.[0] as any;
        const url = uploaded?.url || uploaded?.formats?.url;
        if (!url) {
          throw new Error("Upload did not return a URL");
        }
        return buildMediaUrl(url);
      } catch (e: any) {
        addNotification(e?.message || "Failed to upload image", "error");
        throw e;
      }
    },
    [addNotification]
  );

  const saveArticle = async (
    data: ArticleFormValues,
    status: ArticleStatus
  ): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setUploadingImage(true);

    try {
      let imageId: number | undefined = undefined;

      if (localImageFile) {
        try {
          const response = await uploadArticleImage(localImageFile);
          const uploadedFile = response.data[0];
          imageId = uploadedFile.id;
          setUploadedImageId(uploadedFile.id);
        } catch {
          addNotification("Failed to upload image", "error");
          setIsSubmitting(false);
          setUploadingImage(false);
          return;
        }
      }

      const normalizedBody = normalizeArticleHtml(data.body);

      const playlistValue = selectedPlaylist
        ? selectedPlaylist.documentId
        : articleId
          ? null
          : undefined;

      // Autofill description: trimmed plain text version of article body (150 chars)
      const description = extractPlainText(normalizedBody, 150);

      const articleData: CreateArticleBody = {
        header: data.title || undefined,
        body: normalizedBody,
        // Autofill createdDate: only when article is initially created (backend should also handle this)
        createdDate: !articleId ? new Date().toISOString() : undefined,
        // Autofill publishedDate: only when an admin/manager publishes
        publishedDate:
          status === "published" ? new Date().toISOString() : undefined,
        // Autofill description: trimmed plain text version of article body (150 chars)
        description: description || undefined,
        featuredImage: imageId || uploadedImageId || undefined,
        // allow clearing caption (empty string => null in API layer)
        featuredImageCaption: imageCaption,
        articleStatus: status,
        playlist: playlistValue,
        curator: curator?.documentId || undefined,
      };

      if (articleId) {
        await updateArticleApi(articleId, articleData);
        addNotification(
          status === "published"
            ? "Article published successfully"
            : status === "submitted"
              ? "Article submitted for review"
              : "Article updated successfully",
          "success"
        );
      } else {
        const response = await createArticleApi(articleData);
        const createdArticle = response.data.data;
        setArticleId(createdArticle.documentId);
        addNotification(
          status === "published"
            ? "Article published successfully"
            : status === "submitted"
              ? "Article submitted for review"
              : "Article saved as draft successfully",
          "success"
        );
      }

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ARTICLES],
      });

      // Si se subió una cover, ya no es "local" (evita re-subir)
      if (localImageFile) {
        setLocalImageFile(null);
      }
      // Caption-only updates should also clear dirty once saved
      setCoverDirty(false);
      setPlaylistDirty(false);

      // Marcar el form como "guardado"
      reset(
        { title: data.title || "", body: normalizedBody || "" },
        { keepErrors: true }
      );

      // Limpiar estado después de guardar exitosamente solo si se publicó
      if (status === "published") {
        setLocalImageFile(null);
        setImagePreview(null);
        setUploadedImageId(null);
        setImageCaption("");
        navigate("/articles");
      }
    } catch (error: any) {
      addNotification(
        error?.response?.data?.error?.message || "Failed to save article",
        "error"
      );
    } finally {
      setIsSubmitting(false);
      setUploadingImage(false);
    }
  };

  const onSubmit = async (data: ArticleFormValues): Promise<void> => {
    await saveArticle(data, "draft");
  };

  const handleSave = (): void => {
    if (curatorArticleLocked) return;
    handleSubmit(onSubmit)();
  };

  const handleSubmitArticle = (): void => {
    if (curatorArticleLocked) return;
    handleSubmit(async (data) => {
      await saveArticle(data, "submitted");
    })();
  };

  const handlePublish = (): void => {
    if (!canApprove) return;
    if (isSaveRequired) {
      addNotification("Please save the article before publishing", "error");
      return;
    }
    handleSubmit(async (data) => {
      await saveArticle(data, "published");
    })();
  };

  return (
    <WrapperContainer>
      <PageTopSection>
        <TopBarSection columnCount={3}>
          <TopBarColumn>
            <PageTitle>Curator Articles</PageTitle>
            <ButtonContainer>
              <GoBackButton navigateTo="/articles" label="Back" />
              <DescriptionText>
                Write and publish articles to showcase on your Curator Page.
              </DescriptionText>
            </ButtonContainer>
          </TopBarColumn>
          <TopBarColumn style={{ flex: 1 }} />
          <TopBarColumn
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              paddingBottom: 60,
              alignItems: "flex-end",
              gap: theme.margins.small,
            }}
          >
            <UIButton
              type="button"
              onClick={handleSave}
              variant={isSaveRequired ? "primaryOutline" : "secondaryOutline"}
              style={{ minWidth: 90 }}
              disabled={
                isSubmitting ||
                uploadingImage ||
                uploadingEditorImage ||
                curatorArticleLocked ||
                loadedArticleStatus === "submitted" ||
                loadedArticleStatus === "published"
              }
              tooltipContent={
                loadedArticleStatus === "submitted"
                  ? "Already submitted"
                  : loadedArticleStatus === "published"
                    ? "Already published"
                    : undefined
              }
            >
              {isSubmitting || uploadingImage ? <Spinner small /> : "Save"}
            </UIButton>
            <UIButton
              type="button"
              onClick={handleSubmitArticle}
              variant={hasAnyContent ? "primaryOutline" : "secondaryOutline"}
              style={{ minWidth: 90 }}
              disabled={
                isSubmitting ||
                uploadingImage ||
                uploadingEditorImage ||
                curatorArticleLocked ||
                loadedArticleStatus === "submitted" ||
                loadedArticleStatus === "published"
              }
              tooltipContent={
                loadedArticleStatus === "submitted"
                  ? "Already submitted"
                  : loadedArticleStatus === "published"
                    ? "Already published"
                    : undefined
              }
            >
              {isSubmitting || uploadingImage ? <Spinner small /> : "Submit"}
            </UIButton>
            {canApprove && (
              <UIButton
                type="button"
                onClick={handlePublish}
                variant="primary"
                style={{ minWidth: 90 }}
                disabled={
                  isSubmitting ||
                  uploadingImage ||
                  uploadingEditorImage ||
                  isSaveRequired
                }
              >
                {isSubmitting || uploadingImage ? <Spinner small /> : "Publish"}
              </UIButton>
            )}
          </TopBarColumn>
        </TopBarSection>
      </PageTopSection>
      <ContentContainer
        allowScroll={true}
        contentContainerStyles={{
          flexDirection: "column",
          alignItems: "flex-start",
          padding: `${theme.margins.xl} ${theme.margins.xl}`,
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "100%",
            alignItems: "flex-start",
            minHeight: "fit-content",
            paddingBottom: theme.margins.xl,
            boxSizing: "border-box",
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <MainContentContainer>
            <ArticleLayout>
              <RelatedPlaylistsSection>
                <RelatedPlaylistsLabel>Related Playlists</RelatedPlaylistsLabel>
                <RelatedPlaylistCard
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (curatorArticleLocked) return;
                    openPlaylistModal();
                  }}
                >
                  {selectedPlaylist ? (
                    <>
                      {selectedPlaylist.imageUrl && (
                        <RelatedPlaylistImage
                          src={selectedPlaylist.imageUrl}
                          alt={selectedPlaylist.title || "Playlist"}
                        />
                      )}
                      <RelatedPlaylistTitle>
                        {selectedPlaylist.title || "Untitled playlist"}
                      </RelatedPlaylistTitle>
                      <RelatedPlaylistSubtitle>
                        {selectedPlaylist.showTitle || "Click to change"}
                      </RelatedPlaylistSubtitle>
                    </>
                  ) : (
                    <RelatedPlaylistSubtitle>
                      Click to link a related playlist.
                    </RelatedPlaylistSubtitle>
                  )}
                </RelatedPlaylistCard>
                {selectedPlaylist && (
                  <UIButton
                    type="button"
                    variant="secondaryOutline"
                    style={{ width: "fit-content", minWidth: 90 }}
                    disabled={curatorArticleLocked}
                    onClick={(e) => {
                      e.preventDefault();
                      if (curatorArticleLocked) return;
                      setSelectedPlaylist(null);
                      setPlaylistDirty(true);
                    }}
                  >
                    Clear
                  </UIButton>
                )}
              </RelatedPlaylistsSection>
              <EditorSection>
                {isLoadingArticle && articleIdParam ? (
                  <span style={{ color: theme.colors.gray, fontSize: "14px" }}>
                    Loading article...
                  </span>
                ) : isErrorArticle && articleIdParam ? (
                  <span style={{ color: theme.colors.red, fontSize: "14px" }}>
                    Failed to load article.
                  </span>
                ) : null}
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TitleInput
                        {...field}
                        placeholder="Title"
                        type="text"
                        readOnly={curatorArticleLocked}
                      />
                      {errors.title && (
                        <span
                          style={{
                            color: theme.colors.red,
                            fontSize: "12px",
                            marginTop: "-16px",
                            marginBottom: "16px",
                            display: "block",
                          }}
                        >
                          {errors.title.message}
                        </span>
                      )}
                    </>
                  )}
                />
                <CoverPhotoSection>
                  {imagePreview ? (
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: theme.margins.small,
                      }}
                    >
                      <ImageInputContainer>
                        <CoverPhotoPreview
                          $imageUrl={imagePreview}
                          onClick={() => {
                            if (curatorArticleLocked) return;
                            openModal();
                          }}
                        ></CoverPhotoPreview>
                        <img
                          src={CheckSVG}
                          alt="CheckSVG"
                          style={{
                            width: 20,
                            height: 20,
                            position: "absolute",
                            top: 0,
                            right: 0,
                          }}
                        />
                      </ImageInputContainer>
                      <span
                        style={{ fontSize: "12px", color: theme.colors.gray }}
                      >
                        {imageCaption}
                      </span>
                    </div>
                  ) : (
                    <>
                      <ImageInputContainer>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: theme.margins.small,
                          }}
                        >
                          <span>
                            <img
                              src={UploadIcon}
                              alt="Upload"
                              style={{ width: 20, height: 20 }}
                            />
                          </span>
                          <CoverPhotoButton
                            type="button"
                            disabled={curatorArticleLocked}
                            onClick={(e) => {
                              e.preventDefault();
                              if (curatorArticleLocked) return;
                              openModal();
                            }}
                          >
                            <span>Upload article cover photo here</span>
                          </CoverPhotoButton>
                        </div>
                        <img
                          src={CheckOffSVG}
                          alt="CheckOffSVG"
                          style={{ width: 20, height: 20 }}
                        />
                      </ImageInputContainer>
                    </>
                  )}
                </CoverPhotoSection>
                <Controller
                  name="body"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Write your article here..."
                      readOnly={curatorArticleLocked}
                      onUploadImage={handleUploadInlineImage}
                      onUploadStateChange={setUploadingEditorImage}
                      toolbarRightContent={
                        curatorArticleLocked ? undefined : (
                          <UIButton
                            onClick={handleSave}
                            variant="transparent"
                            tooltipContent="Save"
                            disabled={
                              isSubmitting ||
                              uploadingImage ||
                              uploadingEditorImage
                            }
                            style={{ width: 42, height: 42 }}
                          >
                            {isSubmitting || uploadingImage ? (
                              <Spinner small />
                            ) : (
                              <img src={SaveIcon} alt="Save" />
                            )}
                          </UIButton>
                        )
                      }
                    />
                  )}
                />
                {errors.body && (
                  <span style={{ color: theme.colors.red, fontSize: "12px" }}>
                    {errors.body.message}
                  </span>
                )}
              </EditorSection>
              <PreviewSection>
                <PreviewHeader>
                  <img src={EyeIcon} alt="Preview" style={{ width: 14 }} />
                  Article preview
                </PreviewHeader>
                <PreviewDevice>
                  <PreviewPhoneStatusBar>
                    <span>9:41</span>
                    <span
                      style={{ display: "flex", gap: 6, alignItems: "center" }}
                    >
                      <Signal
                        size={14}
                        color={theme.colors.white}
                        strokeWidth={2}
                      />
                      <Wifi
                        size={14}
                        color={theme.colors.white}
                        strokeWidth={2}
                      />
                      <Battery
                        size={14}
                        color={theme.colors.white}
                        strokeWidth={2}
                      />
                    </span>
                  </PreviewPhoneStatusBar>
                  <PreviewPhoneAppBar>
                    <img
                      src={RovrLogo}
                      alt="ROVR"
                      style={{ height: 26, width: "auto" }}
                    />
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          width: 18,
                          height: 2,
                          backgroundColor: theme.colors.yellow,
                          borderRadius: 1,
                        }}
                      />
                      <span
                        style={{
                          width: 18,
                          height: 2,
                          backgroundColor: theme.colors.yellow,
                          borderRadius: 1,
                        }}
                      />
                      <span
                        style={{
                          width: 18,
                          height: 2,
                          backgroundColor: theme.colors.yellow,
                          borderRadius: 1,
                        }}
                      />
                    </span>
                  </PreviewPhoneAppBar>
                  <PreviewPhoneContent>
                    <PreviewPhoneBack
                      style={{
                        marginTop: theme.margins.medium,
                        marginBottom: theme.margins.medium,
                      }}
                    >
                      <img
                        src={GoBackWebsiteIcon}
                        alt="Back"
                        style={{ width: 29, height: 29 }}
                      />
                    </PreviewPhoneBack>
                    <PreviewMeta>
                      <ArticleSVG $size={14} $active />
                      <span>Created {previewDate}</span>
                      <span style={{ opacity: 0.8 }}>•</span>
                      <span>{previewReadMinutes} min read</span>
                    </PreviewMeta>
                    <PreviewTitle>{previewTitle}</PreviewTitle>
                    <PreviewByline>
                      <span className="by-prefix">BY </span>
                      <span className="by-name">
                        {curator?.name || "curator"}
                      </span>
                    </PreviewByline>
                    <PreviewCoverWrapper>
                      {previewCoverUrl ? (
                        <RovrDuotoneImage
                          src={previewCoverUrl}
                          alt=""
                          variant="svg"
                          intensity="normal"
                          width="100%"
                          height={200}
                          objectFit="cover"
                          loading="eager"
                          style={{ borderRadius: 12 }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: 200,
                            minHeight: 200,
                            backgroundColor: theme.colors.darkBg,
                            borderRadius: 12,
                          }}
                        />
                      )}
                    </PreviewCoverWrapper>
                    {imageCaption.trim() && (
                      <PreviewCaption>{imageCaption}</PreviewCaption>
                    )}
                    <PreviewBody>
                      {previewBodySegments.length > 0 ? (
                        previewBodySegments.map((seg, i) =>
                          seg.type === "html" ? (
                            <div
                              key={i}
                              dangerouslySetInnerHTML={{ __html: seg.content }}
                            />
                          ) : (
                            <div
                              key={i}
                              style={{
                                margin: "16px 0",
                                borderRadius: 12,
                                overflow: "hidden",
                              }}
                            >
                              <RovrDuotoneImage
                                src={seg.src}
                                alt={seg.alt}
                                variant="svg"
                                intensity="normal"
                                width="100%"
                                objectFit="cover"
                                loading="lazy"
                              />
                            </div>
                          )
                        )
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: "<p>Start writing to see the preview.</p>",
                          }}
                        />
                      )}
                    </PreviewBody>
                  </PreviewPhoneContent>
                  <PreviewPhoneBottomTabs>
                    <PreviewTab>RADIO</PreviewTab>
                    <PreviewTab>MOODS</PreviewTab>
                    <PreviewTab>SCHEDULE</PreviewTab>
                    <PreviewTab>CURATORS</PreviewTab>
                    <PreviewTab $active>ARCHIVE</PreviewTab>
                  </PreviewPhoneBottomTabs>
                </PreviewDevice>
              </PreviewSection>
            </ArticleLayout>
            <ImageUploadModal
              isOpen={isModalOpen}
              onClose={closeModal}
              onSave={handleImageSave}
              initialImage={imagePreview}
              initialCaption={imageCaption}
            />
            <UIModal
              isOpen={isPlaylistModalOpen}
              onClose={closePlaylistModal}
              title="Select playlist"
              actions={[
                <UIButton
                  key="close"
                  onClick={closePlaylistModal}
                  variant="secondaryOutline"
                >
                  Close
                </UIButton>,
              ]}
              modalStyles={{ width: 600 }}
            >
              <div style={{ padding: theme.margins.medium }}>
                <SearchInput
                  placeholder="Search playlists"
                  value={playlistSearchText}
                  onChange={(e) => setPlaylistSearchText(e.target.value)}
                />
                <div style={{ height: theme.margins.medium }} />
                <PlaylistListContainer>
                  {isLoadingPlaylists && playlists.length === 0 ? (
                    <span style={{ color: theme.colors.gray }}>
                      Loading playlists...
                    </span>
                  ) : playlists.length === 0 ? (
                    <span style={{ color: theme.colors.gray }}>
                      No playlists found.
                    </span>
                  ) : (
                    playlists.map((playlist) => {
                      const activeId =
                        playlist.documentId === selectedPlaylist?.documentId;
                      const playlistImageUrl = getPlaylistImageUrl(playlist);
                      return (
                        <PlaylistListItem
                          key={playlist.documentId}
                          type="button"
                          $active={activeId}
                          onClick={() => {
                            setSelectedPlaylist({
                              documentId: playlist.documentId,
                              title: playlist.title,
                              showTitle: playlist.show?.title,
                              imageUrl: playlistImageUrl || undefined,
                            });
                            setPlaylistDirty(true);
                            closePlaylistModal();
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: theme.margins.small,
                              width: "100%",
                            }}
                          >
                            <ArtWorkImage
                              value={playlistImageUrl}
                              customSize="40px"
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>
                                {playlist.title}
                              </span>
                              <span
                                style={{
                                  color: theme.colors.white,
                                  fontSize: 12,
                                }}
                              >
                                {playlist.show?.title || "No show"}
                              </span>
                            </div>
                          </div>
                        </PlaylistListItem>
                      );
                    })
                  )}
                  <div ref={playlistsEndRef} />
                  {isFetchingNextPlaylists && (
                    <span style={{ color: theme.colors.gray }}>
                      Loading more...
                    </span>
                  )}
                </PlaylistListContainer>
              </div>
            </UIModal>
          </MainContentContainer>
        </form>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default WriteArticlePage;
